import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { RecoveryFlowState, type Session } from '@ory/client'

import { fetchRecoveryFlow, fetchWhoami } from '@utils/kratos.utils'
import { logClientError, translateClientError } from '@i18n/kratos/kratos-client-error'

export type InviteState =
  | { kind: 'loading' }
  // Code still has to be redeemed before the password can be set.
  | { kind: 'redeem' }
  // Live invitation whose code is not in the URL — a reload after it was
  // stripped, most often. The code is still in the mail, so ask for it rather
  // than declaring a perfectly good invitation dead.
  | { kind: 'needsCode' }
  // A recovery session is already live — the code was spent on an earlier
  // attempt whose password Kratos rejected. Skip straight to the password.
  | { kind: 'resume' }
  | { kind: 'used' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string }

const LOADING: InviteState = { kind: 'loading' }

// A live session on this browser may belong to somebody else entirely — a
// colleague who never signed out. Resuming into it would point the password
// form at *their* account, so the session only counts when it is provably the
// invitee's. No invited address to compare against means no resume.
const sessionBelongsToInvitee = (session: Session, invitedEmail: string | null): boolean => {
  if (!invitedEmail) {
    return false
  }
  const traits = session.identity?.traits as { email?: unknown } | undefined
  const sessionEmail = typeof traits?.email === 'string' ? traits.email : null
  return sessionEmail !== null && sessionEmail.toLowerCase() === invitedEmail.toLowerCase()
}

// A spent code leaves the flow at `passed_challenge` forever, so that state
// alone cannot tell "someone else already used this link" from "the invitee
// reloaded after Kratos rejected their password". The live session settles it
// — but only there: a matching session on a flow that is still `sent_email`
// means an already-signed-in invitee, and handing them to /settings would
// quietly throw away a code that was never redeemed.
export const useInviteState = (
  flowId: string | null,
  code: string | null,
  invitedEmail: string | null,
): InviteState => {
  const intl = useIntl()
  const intlRef = useRef(intl)
  useEffect(() => {
    intlRef.current = intl
  }, [intl])

  const [state, setState] = useState<InviteState>(LOADING)

  useEffect(() => {
    setState(LOADING)
    const controller = new AbortController()

    const resolve = async (): Promise<InviteState> => {
      if (!flowId) {
        return { kind: 'expired' }
      }

      const result = await fetchRecoveryFlow(flowId, controller.signal)
      if (result.kind === 'error') {
        logClientError(result.code, result.message)
        return { kind: 'error', message: translateClientError(intlRef.current, result.code) }
      }
      if (result.kind === 'expired') {
        return { kind: 'expired' }
      }

      if (result.flow.state === RecoveryFlowState.PassedChallenge) {
        const who = await fetchWhoami(controller.signal)
        return who.kind === 'authenticated' && sessionBelongsToInvitee(who.session, invitedEmail)
          ? { kind: 'resume' }
          : { kind: 'used' }
      }
      // Admin-issued flows are created straight into `sent_email`; any other
      // state means this link did not come from an invitation.
      if (result.flow.state !== RecoveryFlowState.SentEmail) {
        return { kind: 'expired' }
      }
      return code ? { kind: 'redeem' } : { kind: 'needsCode' }
    }

    resolve().then((next) => {
      if (!controller.signal.aborted) {
        setState(next)
      }
    })

    return () => {
      controller.abort()
    }
  }, [flowId, code, invitedEmail])

  return state
}
